import React, { useState, useEffect } from 'react';
import apiClient from '../api/axiosConfig';
import { TrashIcon, PlusIcon } from '@heroicons/react/24/outline';
import Pagination from '../components/Pagination';

interface ExcludedEntity {
  id: number;
  name: string;
  type: 'network' | 'group';
}

const ExcludedEntitiesPage: React.FC = () => {
  const [excludedEntities, setExcludedEntities] = useState<ExcludedEntity[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRemovingEntity, setIsRemovingEntity] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isAddFormVisible, setIsAddFormVisible] = useState(false);
  const [newEntityId, setNewEntityId] = useState('');
  const [newEntityType, setNewEntityType] = useState<'network' | 'group'>('network');

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20; // Number of items to display per page

  // Fetch excluded entities on component mount
  useEffect(() => {
    fetchExcludedEntities();
  }, []);

  // Filter entities based on search term
  const filteredEntities = excludedEntities.filter(entity =>
    entity.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    entity.id.toString().includes(searchTerm)
  );

  // Get current page items
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredEntities.slice(indexOfFirstItem, indexOfLastItem);

  // Handle page change
  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  // Fetch excluded entities from the API
  const fetchExcludedEntities = async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Fetch excluded networks with names
      const networksResponse = await apiClient.get<ExcludedEntity[]>('/api/excluded-entities/networks');
      const networkEntities = networksResponse.data;

      // Fetch excluded groups with names
      const groupsResponse = await apiClient.get<ExcludedEntity[]>('/api/excluded-entities/groups');
      const groupEntities = groupsResponse.data;

      // Combine the networks and groups
      setExcludedEntities([...networkEntities, ...groupEntities]);
    } catch (error) {
      console.error('Failed to fetch excluded entities:', error);
      setError('Failed to load excluded entities. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Remove an entity from the excluded list
  const handleRemoveEntity = async (entity: ExcludedEntity) => {
    try {
      // Set the loading state for this specific entity
      setIsRemovingEntity(entity.id);

      if (entity.type === 'network') {
        await apiClient.post('/api/excluded-entities/networks/remove', { networkId: entity.id });
      } else {
        await apiClient.post('/api/excluded-entities/groups/remove', { groupId: entity.id });
      }

      // Refresh the list after successful removal
      fetchExcludedEntities();

      // Check if we need to go to the previous page (if current page becomes empty)
      const remainingItems = filteredEntities.filter(e => !(e.id === entity.id && e.type === entity.type));
      const totalPages = Math.ceil(remainingItems.length / itemsPerPage);
      if (currentPage > totalPages && totalPages > 0) {
        setCurrentPage(totalPages);
      }
    } catch (error) {
      console.error('Failed to remove entity:', error);
      setError('Failed to remove entity. Please try again.');
    } finally {
      // Clear the loading state
      setIsRemovingEntity(null);
    }
  };

  // State for the new entity name
  const [newEntityName, setNewEntityName] = useState('');

  // Add a new entity to the excluded list
  const handleAddEntity = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!newEntityId) {
      setError('Entity ID is required');
      return;
    }

    try {
      const id = parseInt(newEntityId);

      if (isNaN(id)) {
        setError('Entity ID must be a number');
        return;
      }

      if (!newEntityName) {
        setError('Entity name is required');
        return;
      }

      if (newEntityType === 'network') {
        await apiClient.post('/api/excluded-entities/networks/add', { 
          networkId: id,
          name: newEntityName
        });
      } else {
        await apiClient.post('/api/excluded-entities/groups/add', { 
          groupId: id,
          name: newEntityName
        });
      }

      // Reset form
      setNewEntityId('');
      setNewEntityName('');
      setIsAddFormVisible(false);

      // Refresh the list after successful addition
      fetchExcludedEntities();

      // Reset to first page when a new entity is added
      setCurrentPage(1);
    } catch (error) {
      console.error('Failed to add entity:', error);
      setError('Failed to add entity. Please try again.');
    }
  };

  // Toggle the add form visibility
  const toggleAddForm = () => {
    setIsAddFormVisible(!isAddFormVisible);
    setError(null);
  };

  return (
    <div className="space-y-8">
      <div className="bg-white shadow sm:rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-lg font-medium leading-6 text-gray-900">
                Excluded Entities
              </h3>
              <div className="mt-2 max-w-xl text-sm text-gray-500">
                <p>
                  These entities are excluded from country mappings and will not be included in reports.
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={toggleAddForm}
              className="inline-flex items-center rounded-md border border-transparent bg-primary px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
            >
              <PlusIcon className="-ml-1 mr-2 h-5 w-5" aria-hidden="true" />
              Add Entity
            </button>
          </div>

          {/* Add form */}
          {isAddFormVisible && (
            <div className="mt-5 border border-gray-200 rounded-md p-4">
              <h4 className="text-md font-medium text-gray-900 mb-3">Add Excluded Entity</h4>
              <form onSubmit={handleAddEntity}>
                <div className="grid grid-cols-1 gap-y-4 sm:grid-cols-2 sm:gap-x-4">
                  <div>
                    <label htmlFor="entityId" className="block text-sm font-medium text-gray-700">
                      Entity ID <span className="text-red-500">*</span>
                    </label>
                    <div className="mt-1">
                      <input
                        type="text"
                        name="entityId"
                        id="entityId"
                        value={newEntityId}
                        onChange={(e) => setNewEntityId(e.target.value)}
                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                        placeholder="Enter entity ID"
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="entityType" className="block text-sm font-medium text-gray-700">
                      Entity Type
                    </label>
                    <div className="mt-1">
                      <select
                        id="entityType"
                        name="entityType"
                        value={newEntityType}
                        onChange={(e) => setNewEntityType(e.target.value as 'network' | 'group')}
                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                      >
                        <option value="network">Network</option>
                        <option value="group">Group</option>
                      </select>
                    </div>
                  </div>
                  <div className="sm:col-span-2">
                    <label htmlFor="entityName" className="block text-sm font-medium text-gray-700">
                      Name <span className="text-red-500">*</span>
                    </label>
                    <div className="mt-1">
                      <input
                        type="text"
                        name="entityName"
                        id="entityName"
                        value={newEntityName}
                        onChange={(e) => setNewEntityName(e.target.value)}
                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                        placeholder="Enter a descriptive name"
                        required
                      />
                    </div>
                  </div>
                </div>
                <div className="mt-4 flex justify-end">
                  <button
                    type="button"
                    onClick={toggleAddForm}
                    className="mr-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="inline-flex items-center rounded-md border border-transparent bg-primary px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                  >
                    Add
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Search input */}
          <div className="mt-5">
            <label htmlFor="search" className="block text-sm font-medium text-gray-700">
              Search
            </label>
            <div className="mt-1">
              <input
                type="text"
                name="search"
                id="search"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1); // Reset to first page on search
                }}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                placeholder="Search by name or ID"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="rounded-md bg-boundlessBlue p-4">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-white">
                {error}
              </h3>
            </div>
          </div>
        </div>
      )}

      {/* Loading state */}
      {isLoading ? (
        <div className="text-center">
          <p className="text-gray-500">Loading excluded entities...</p>
        </div>
      ) : (
        <>
          {/* Entities table */}
          {filteredEntities.length > 0 ? (
            <div className="bg-white shadow overflow-hidden sm:rounded-md">
              <ul className="divide-y divide-gray-200">
                {currentItems.map((entity) => (
                  <li key={`${entity.type}-${entity.id}`}>
                    <div className="px-4 py-4 flex items-center sm:px-6">
                      <div className="min-w-0 flex-1 sm:flex sm:items-center sm:justify-between">
                        <div>
                          <div className="flex text-sm">
                            <p className="font-medium text-primary truncate">{entity.name}</p>
                            <p className="ml-1 flex-shrink-0 font-normal text-gray-500">
                              (ID: {entity.id})
                            </p>
                          </div>
                          <div className="mt-2 flex">
                            <div className="flex items-center text-sm text-gray-500">
                              <p>
                                Type: {entity.type === 'network' ? 'Network' : 'Group'}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="ml-5 flex-shrink-0">
                        <button
                          type="button"
                          onClick={() => handleRemoveEntity(entity)}
                          disabled={isRemovingEntity === entity.id}
                          className={`inline-flex items-center rounded-md border border-transparent bg-accent px-3 py-2 text-sm font-medium leading-4 text-white shadow-sm hover:bg-accent-dark focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2 ${isRemovingEntity === entity.id ? 'opacity-75 cursor-not-allowed' : ''}`}
                        >
                          <TrashIcon className="-ml-0.5 mr-2 h-4 w-4" aria-hidden="true" />
                          {isRemovingEntity === entity.id ? 'Removing...' : 'Remove'}
                        </button>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>

              {/* Pagination */}
              <Pagination
                currentPage={currentPage}
                totalItems={filteredEntities.length}
                itemsPerPage={itemsPerPage}
                onPageChange={handlePageChange}
              />
            </div>
          ) : (
            <div className="text-center py-12 bg-white shadow sm:rounded-lg">
              <p className="text-gray-500">
                {searchTerm ? 'No entities match your search.' : 'No excluded entities found.'}
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ExcludedEntitiesPage;
